import { useState, useEffect, useCallback } from 'react';
import { api, PaymentHistoryItem } from '../../lib/api';

export function PaymentHistoryTable() {
  const [payments, setPayments] = useState<PaymentHistoryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const limit = 10;

  const fetchPayments = useCallback(async (fetchOffset: number, append: boolean) => {
    if (append) setLoadingMore(true);
    else setLoading(true);

    try {
      const result = await api.getPaymentHistory({ limit, offset: fetchOffset });
      if (append) {
        setPayments((prev) => [...prev, ...result.payments]);
      } else {
        setPayments(result.payments);
      }
      setTotal(result.total);
      setOffset(fetchOffset + result.payments.length);
    } catch {
      // Silent fail -- payment history is non-critical
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    fetchPayments(0, false);
  }, [fetchPayments]);

  const hasMore = offset < total;

  const statusBadge = (status: PaymentHistoryItem['status']) => {
    const styles: Record<string, string> = {
      succeeded: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      refunded: 'bg-gray-100 text-gray-800 dark:bg-gray-800/50 dark:text-gray-400',
      partially_refunded: 'bg-gray-100 text-gray-800 dark:bg-gray-800/50 dark:text-gray-400',
    };

    const labels: Record<string, string> = {
      succeeded: 'Succeeded',
      pending: 'Pending',
      failed: 'Failed',
      refunded: 'Refunded',
      partially_refunded: 'Partial Refund',
    };

    return (
      <span
        className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${styles[status] ?? ''}`}
      >
        {labels[status] ?? status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg shadow-sm border border-border p-6">
        <div className="animate-pulse">
          <div className="h-5 bg-muted rounded w-40 mb-4"></div>
          <div className="space-y-3">
            <div className="h-8 bg-muted rounded"></div>
            <div className="h-8 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (payments.length === 0) {
    return (
      <div className="bg-card rounded-lg shadow-sm border border-border p-6">
        <h2 className="text-lg font-semibold text-foreground mb-2">Payment History</h2>
        <p className="text-sm text-muted-foreground">
          No payment history yet. Purchase credits to get started.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border p-6">
      <h2 className="text-lg font-semibold text-foreground mb-4">Payment History ({total})</h2>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-2 text-muted-foreground font-medium">Date</th>
              <th className="text-left py-2 text-muted-foreground font-medium">Amount</th>
              <th className="text-left py-2 text-muted-foreground font-medium">Credits</th>
              <th className="text-left py-2 text-muted-foreground font-medium">Method</th>
              <th className="text-left py-2 text-muted-foreground font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {payments.map((payment) => (
              <tr key={payment.paymentId} className="border-b border-border/50">
                <td className="py-2.5 text-foreground">
                  {new Date(payment.createdAt).toLocaleDateString()}
                </td>
                <td className="py-2.5 text-foreground font-medium">{payment.displayAmount}</td>
                <td className="py-2.5 text-foreground">{payment.creditsPurchased}</td>
                <td className="py-2.5 text-muted-foreground capitalize">
                  {payment.paymentProvider === 'coinbase' ? 'Crypto' : 'Card'}
                </td>
                <td className="py-2.5">{statusBadge(payment.status)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <button
          onClick={() => fetchPayments(offset, true)}
          disabled={loadingMore}
          className="mt-4 w-full py-2 text-sm text-primary hover:text-primary/80 disabled:opacity-50"
        >
          {loadingMore ? 'Loading...' : 'Load more'}
        </button>
      )}
    </div>
  );
}
